import React, { useState, useEffect } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import "./ProfileUpdate.css";
import DynamicFormMain from "../components/Profile&Settings/CommunityProfile/DynamicFormMain";
import { useAuthContext } from '../hooks/useAuthContext';
import Alerts from '../components/Profile&Settings/AlertsSettings';
import { getAuth, signOut } from 'firebase/auth';
import firebaseApp from '../Firebase';
import { useNavigate } from 'react-router-dom';
import DeletionCountdown from '../components/DeletionCountdown';

const Settings = () => {
  const [expanded, setExpanded] = useState(false);
  const [communityNames, setCommunityNames] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRequestingGracePeriod, setIsRequestingGracePeriod] = useState(false);
  const [isCancellingGracePeriod, setIsCancellingGracePeriod] = useState(false);
  const [showGracePeriodInfo, setShowGracePeriodInfo] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [leaveConfirm, setLeaveConfirm] = useState(null); // communityId being confirmed
  const [isLeaving, setIsLeaving] = useState(false);
  const { user, refreshUserProfile, dispatch } = useAuthContext();
  const navigate = useNavigate();
  const auth = getAuth(firebaseApp);

  console.log('⚙️ [SETTINGS DEBUG] Settings component rendered with user:', {
    hasUser: !!user,
    userId: user?.userId,
    listOfCommunities: user?.listOfCommunities,
    communityCount: user?.listOfCommunities?.length || 0
  });

  // Refresh user profile on component mount to ensure latest community list
  useEffect(() => {
    if (user && typeof refreshUserProfile === 'function') {
      console.log('🔄 [SETTINGS] Refreshing user profile to get latest communities');
      refreshUserProfile();
    }
  }, []); // Only run once on mount

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleLeaveCommunity = async (communityId) => {
    setIsLeaving(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/leave-community/${communityId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to leave community');
      const data = await res.json();
      const updated = { ...user, listOfCommunities: data.listOfCommunities };
      localStorage.setItem('user', JSON.stringify(updated));
      dispatch({ type: 'LOGIN', payload: updated });
    } catch (err) {
      alert('Failed to leave community. Please try again.');
    } finally {
      setIsLeaving(false);
      setLeaveConfirm(null);
    }
  };

  const handleCommunityName = (communityName, index) => {
    setCommunityNames(prevNames => {
      const newNames = [...prevNames];
      newNames[index] = communityName;
      return newNames;
    });
  };

  const handleDeleteAccount = async () => {
    if (!auth.currentUser) {
      alert('No user logged in');
      return;
    }

    setIsDeleting(true);
    try {
      console.log('🗑️ [DELETE ACCOUNT] Starting account deletion process...');
      console.log('🗑️ [DELETE ACCOUNT] User ID:', user?.userId);
      console.log('🗑️ [DELETE ACCOUNT] Firebase UID:', auth.currentUser?.uid);

      // Delete user data from backend first
      const token = await auth.currentUser.getIdToken();
      const deleteUrl = `${process.env.REACT_APP_API_BASE_URL}/user/delete`;
      console.log('🗑️ [DELETE ACCOUNT] Calling backend delete endpoint...');
      console.log('🗑️ [DELETE ACCOUNT] Delete URL:', deleteUrl);
      console.log('🗑️ [DELETE ACCOUNT] API Base URL from env:', process.env.REACT_APP_API_BASE_URL);
      
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🗑️ [DELETE ACCOUNT] Response status:', response.status);
      console.log('🗑️ [DELETE ACCOUNT] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🗑️ [DELETE ACCOUNT] Backend deletion failed:', errorText);
        
        // Try to parse as JSON to get detailed error info
        let errorDetails;
        try {
          errorDetails = JSON.parse(errorText);
        } catch (e) {
          // If not JSON, use the raw text
          errorDetails = { error: errorText };
        }
        
        // Handle specific error types
        if (response.status === 404 && errorDetails.details) {
          console.error('🗑️ [DELETE ACCOUNT] User profile mismatch:', errorDetails);
          throw new Error(`Account sync issue: ${errorDetails.details} ${errorDetails.suggestion || ''}`);
        }
        
        throw new Error('Failed to delete user data from backend');
      }

      const deletionResult = await response.json();
      console.log('✅ [DELETE ACCOUNT] Backend deletion result:', deletionResult);
      console.log('✅ [DELETE ACCOUNT] Total records deleted:', deletionResult.totalRecordsDeleted);
      console.log('✅ [DELETE ACCOUNT] Firebase deleted:', deletionResult.firebaseDeleted);
      console.log('✅ [DELETE ACCOUNT] Deletion breakdown:', deletionResult.deletionBreakdown);

      // Check if deletion was completely successful
      if (deletionResult.success && deletionResult.firebaseDeleted) {
        console.log('✅ [DELETE ACCOUNT] Complete deletion successful - both MongoDB and Firebase');
        alert('Your account has been deleted successfully. You have been logged out.');
      } else if (!deletionResult.firebaseDeleted) {
        console.log('⚠️ [DELETE ACCOUNT] Partial deletion - Firebase user may still exist');
        alert('Your account data has been deleted, but there may be authentication remnants. If you experience login issues, please contact support.');
      } else if (deletionResult.errors.length > 0) {
        console.log('⚠️ [DELETE ACCOUNT] Deletion completed with some errors:', deletionResult.errors);
        alert('Your account has been deleted with some minor issues. You have been logged out.');
      }

      // Log out the user from the app state (always do this)
      console.log('🗑️ [DELETE ACCOUNT] Logging out user from app...');
      dispatch({ type: 'LOGOUT' });
      
      // Clear localStorage to prevent auto-login
      localStorage.removeItem('user');
      console.log('🗑️ [DELETE ACCOUNT] Cleared user data from localStorage');

      // Sign out from Firebase (cleanup - this is safe even if Firebase user was deleted)
      try {
        await signOut(auth);
        console.log('✅ [DELETE ACCOUNT] User signed out from Firebase');
      } catch (signOutError) {
        console.log('ℹ️ [DELETE ACCOUNT] Firebase sign out not needed (user likely already deleted)');
      }
      
      console.log('✅ [DELETE ACCOUNT] Account deletion process completed at:', new Date().toISOString());
      navigate('/');
    } catch (error) {
      console.error('❌ [DELETE ACCOUNT] Error deleting account:', error);
      console.error('❌ [DELETE ACCOUNT] Error message:', error.message);
      alert('Failed to delete account. Please try again or contact support.');
    } finally {
      setIsDeleting(false);
      setShowGracePeriodInfo(false);
      setShowFinalDeleteConfirm(false);
    }
  };

  const handleRequestGracePeriod = async () => {
    if (!auth.currentUser) return;
    setIsRequestingGracePeriod(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/request-deletion-grace-period`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to start grace period');
      await refreshUserProfile();
      setShowGracePeriodInfo(false);
      setShowFinalDeleteConfirm(false);
    } catch (err) {
      alert('Something went wrong. Please try again.');
    } finally {
      setIsRequestingGracePeriod(false);
    }
  };

  const handleCancelGracePeriod = async () => {
    if (!auth.currentUser) return;
    setIsCancellingGracePeriod(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/cancel-deletion-grace-period`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to cancel grace period');
      await refreshUserProfile();
    } catch (err) {
      alert('Something went wrong. Please try again.');
    } finally {
      setIsCancellingGracePeriod(false);
    }
  };

  return (
    <div className="container profile-update-container">
      <div className="MainTitle">
        <h1>Settings &amp; Preferences</h1>
      </div>
      <div className="settings-content">
        <div className="accordion-container">
          {/* Alerts Accordion */}
          <Accordion
            expanded={expanded === 'panel0'}
            onChange={handleChange('panel0')}
            className="profile-accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className="custom-accordion-summary"
            >
              <h2>General</h2>
            </AccordionSummary>
            <AccordionDetails>
              <Alerts />
            </AccordionDetails>
          </Accordion>

          {/* Account Management Accordion — kept separate and collapsed by
              default so the "Danger Zone" styling doesn't show until the
              user deliberately opens this section. */}
          <Accordion
            expanded={expanded === 'panelAccount'}
            onChange={handleChange('panelAccount')}
            className="profile-accordion"
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className="custom-accordion-summary"
            >
              <h2>Account Management</h2>
            </AccordionSummary>
            <AccordionDetails>
              <div className="settings-section account-deletion">
                <div className="danger-zone">
                  <h4>Danger Zone</h4>

                  {user?.pendingDeletion?.status === 'active' && (
                    <div className="grace-period-banner">
                      <p>
                        <strong>Your account is scheduled to be deleted automatically</strong> unless you're
                        matched with someone before then — we'll cancel it the moment you show interest in anyone we suggest.
                        When the timer runs out, your account and all associated data will be permanently deleted. This cannot be undone.
                      </p>
                      <DeletionCountdown targetDate={user.pendingDeletion.autoDeleteAt} />
                      <div className="grace-period-modal-buttons">
                        <button
                          className="grace-period-btn grace-period-btn--green"
                          onClick={handleCancelGracePeriod}
                          disabled={isCancellingGracePeriod}
                        >
                          {isCancellingGracePeriod ? 'Cancelling...' : 'Actually, keep my account open'}
                        </button>
                        <button
                          className="grace-period-modal-skip"
                          onClick={() => { setShowGracePeriodInfo(true); setShowFinalDeleteConfirm(true); }}
                        >
                          Actually, please delete my account
                        </button>
                      </div>
                    </div>
                  )}

                  {user?.pendingDeletion?.status !== 'active' && (
                    <>
                      <p>Once you delete your account, there is no going back. Please be certain.</p>

                      <button
                        className="delete-account-btn"
                        onClick={() => setShowGracePeriodInfo(true)}
                      >
                        Delete Account
                      </button>
                    </>
                  )}

                  {showGracePeriodInfo && (
                    <div className="grace-period-modal-overlay" onClick={() => { setShowGracePeriodInfo(false); setShowFinalDeleteConfirm(false); }}>
                      <div className="grace-period-modal" onClick={(e) => e.stopPropagation()}>
                        {!showFinalDeleteConfirm ? (
                          <>
                            <h3>Give it a chance!</h3>
                            <p className="grace-period-modal-intro">
                              Why not set your account to delete in 30 days if we haven't been able to find anyone?
                            </p>
                            <p className="grace-period-modal-list-preface">
                              Reasons to wait 30 days:
                            </p>
                            <ul className="grace-period-modal-list">
                              <li>We suggest on quality, not quantity.</li>
                              <li>We won't bombard you with suggestions, only when there's truly a good fit.</li>
                              <li>This group is new, and people are joining all the time.</li>
                              <li>If someone joins who is a good fit, we'll let you know.</li>
                              <li>It's very likely someone like you will join in a few weeks — but if you delete now, you'd be two passing ships in the night!</li>
                            </ul>
                            <div className="grace-period-modal-buttons">
                              <button
                                className="grace-period-btn"
                                onClick={handleRequestGracePeriod}
                                disabled={isRequestingGracePeriod}
                              >
                                {isRequestingGracePeriod ? 'Setting up...' : 'Set to delete in 30 days'}
                              </button>
                              <button
                                className="cancel-delete-btn"
                                onClick={() => setShowGracePeriodInfo(false)}
                                disabled={isRequestingGracePeriod}
                              >
                                Cancel
                              </button>
                            </div>
                            <button
                              className="grace-period-modal-skip"
                              onClick={() => setShowFinalDeleteConfirm(true)}
                            >
                              Proceed to delete now
                            </button>
                          </>
                        ) : (
                          <>
                            <h3>Please confirm</h3>
                            <p>This will permanently delete your account and all associated data right now. This cannot be undone.</p>
                            <div className="grace-period-modal-buttons">
                              <button
                                className="confirm-delete-btn"
                                onClick={handleDeleteAccount}
                                disabled={isDeleting}
                              >
                                {isDeleting ? 'Deleting...' : 'Yes, delete my account'}
                              </button>
                              <button
                                className="cancel-delete-btn"
                                onClick={() => setShowFinalDeleteConfirm(false)}
                                disabled={isDeleting}
                              >
                                No, go back
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Community Settings */}
          {Array.isArray(user.listOfCommunities) && 
            user.listOfCommunities.map((community, index) => {
              const communityId = community._id || community;
              return (
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index + 1}`}
                  onChange={handleChange(`panel${index + 1}`)}
                  className="profile-accordion"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="custom-accordion-summary"
                  >
                    <h2>
                      {communityNames[index] || 'Community'}
                    </h2>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DynamicFormMain
                      user={user}
                      communityId={communityId.toString()}
                      onCommunityName={(name) => handleCommunityName(name, index)}
                      setCommunityProfileComplete={() => {}}
                      mode="Settings"
                    />
                    <div className="leave-community-section">
                      {leaveConfirm === communityId.toString() ? (
                        <div className="leave-community-confirm">
                          <p>Are you sure you want to leave <strong>{communityNames[index] || 'this community'}</strong>?</p>
                          <div className="leave-community-buttons">
                            <button className="confirm-leave-btn" onClick={() => handleLeaveCommunity(communityId.toString())} disabled={isLeaving}>
                              {isLeaving ? 'Leaving…' : 'Yes, leave'}
                            </button>
                            <button className="cancel-leave-btn" onClick={() => setLeaveConfirm(null)} disabled={isLeaving}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button className="leave-community-btn" onClick={() => setLeaveConfirm(communityId.toString())}>
                          Leave community
                        </button>
                      )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              );
            })
          }
        </div>
      </div>
    </div>
  );
};

export default Settings;


//   import React, { useState } from 'react';
//   import "./Settings.css";
//   import CommuniSettings from "../components/Profile&Settings/CommuniSettings";
//   import { useAuthContext } from '../hooks/useAuthContext';
//   import Alerts from '../components/Profile&Settings/AlertsSettings';

//   const Settings = () => {
//     const [communityNames, setCommunityNames] = useState([]);
//     const { user } = useAuthContext();


//     const handleCommunityName = (communityName, index) => {
//       setCommunityNames(prevNames => {
//         const newNames = [...prevNames];
//         newNames[index] = communityName;
//         return newNames;
//       });
//     };

//     return (
//       <div className="settings-container">
//   <div className="settings-header">
//     <h1>Settings</h1>
//   </div>
//   <div className="settings-content">
//     <div className="settings-alert">
//       <h2>General</h2>
//       <h4>Notifications</h4>
//       <Alerts />
//     </div>
//     <h2>Community</h2>
//     {Array.isArray(user.listOfCommunities) && user.listOfCommunities.map((communityId, index) => (
//       <div className="communi-settings-section" key={index}>
//         <CommuniSettings 
//           user={user}
//           communityId={communityId.toString()}
//           onCommunityName={(name) => handleCommunityName(name, index)}
//         />
//       </div>
//     ))}
//   </div>
// </div>

//     );
//   };

//   export default Settings;
